import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Inbox as InboxIcon, AlertTriangle, Clock, CheckCircle2, ArrowRight,
  FileText, Calendar, Eye
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const SEVERITY_STYLES = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  LOW: 'bg-green-100 text-green-700 border-green-200',
};

const STATUS_LABELS = {
  RECEBIDO: 'Recebido',
  CLASSIFICADO: 'Classificado',
  PLANEJADO: 'Planejado',
  EM_EXECUCAO: 'Em Execução',
  AGUARDANDO_EVIDENCIA: 'Aguardando Evidência',
  EM_REVISAO: 'Em Revisão',
  RESPONDIDO: 'Respondido',
  ENCERRADO: 'Encerrado',
  ARQUIVADO: 'Arquivado',
};

const computeSlaBucket = (act) => {
  if (act.status === 'ENCERRADO' || act.status === 'ARQUIVADO') return 'concluido';
  const days = differenceInDays(new Date(act.deadline), new Date());
  if (days < 0) return 'vencido';
  if (days <= 7) return 'urgente';
  if (days <= 30) return 'proximo';
  return 'no_prazo';
};

export const InboxPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/acts', { params: { limit: 100 } });
        setActs(res.data.items || res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api]);

  const myActs = acts.filter(a => 
    a.responsible_user_id === user?.user_id || a.created_by === user?.user_id
  );
  
  // Se usuário tem perfil de visão estratégica (Governador, Casa Civil, Admin), mostra TODOS
  const visionRoles = ['GOVERNADOR', 'CASA_CIVIL', 'ADMIN_ESTADUAL', 'CGE', 'CONTROLE_INTERNO', 'AUDITORIA'];
  const baseActs = visionRoles.includes(user?.role) ? acts : (myActs.length > 0 ? myActs : acts);

  const groups = {
    vencido: baseActs.filter(a => computeSlaBucket(a) === 'vencido'),
    urgente: baseActs.filter(a => computeSlaBucket(a) === 'urgente'),
    proximo: baseActs.filter(a => computeSlaBucket(a) === 'proximo'),
    no_prazo: baseActs.filter(a => computeSlaBucket(a) === 'no_prazo'),
    concluido: baseActs.filter(a => computeSlaBucket(a) === 'concluido'),
  };

  const filtered = filter === 'todos' ? baseActs : groups[filter] || [];

  const Stat = ({ label, count, icon: Icon, color, onClick, active }) => (
    <button
      onClick={onClick}
      data-testid={`inbox-stat-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={`flex-1 text-left p-4 rounded-lg border transition-all ${
        active ? 'border-[#1d4ed8] shadow-md bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="text-2xl font-bold text-slate-900">{count}</span>
      </div>
      <p className="text-xs text-slate-600">{label}</p>
    </button>
  );

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto" data-testid="inbox-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <InboxIcon className="w-6 h-6 text-[#1d4ed8]" />
            Meus Atos
          </h1>
          <p className="text-slate-500">
            {visionRoles.includes(user?.role) 
              ? 'Visão consolidada de todos os atos do órgão' 
              : 'Atos sob sua responsabilidade ou criados por você'}
          </p>
        </div>

        {/* Stats cards (clicáveis para filtrar) */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Stat label="Vencidos" count={groups.vencido.length} icon={AlertTriangle} color="text-red-600"
            onClick={() => setFilter('vencido')} active={filter === 'vencido'} />
          <Stat label="Urgentes (≤7 dias)" count={groups.urgente.length} icon={Clock} color="text-orange-600"
            onClick={() => setFilter('urgente')} active={filter === 'urgente'} />
          <Stat label="Próximos (≤30 dias)" count={groups.proximo.length} icon={Calendar} color="text-yellow-600"
            onClick={() => setFilter('proximo')} active={filter === 'proximo'} />
          <Stat label="No Prazo" count={groups.no_prazo.length} icon={FileText} color="text-emerald-600"
            onClick={() => setFilter('no_prazo')} active={filter === 'no_prazo'} />
          <Stat label="Concluídos" count={groups.concluido.length} icon={CheckCircle2} color="text-blue-600"
            onClick={() => setFilter('concluido')} active={filter === 'concluido'} />
        </div>

        {/* Botão Todos */}
        <div className="mb-4 flex justify-between items-center">
          <div className="text-sm text-slate-600">
            Mostrando <span className="font-bold text-slate-900">{filtered.length}</span> de {baseActs.length} atos
          </div>
          {filter !== 'todos' && (
            <Button variant="ghost" size="sm" onClick={() => setFilter('todos')} data-testid="clear-filter-btn">
              Limpar filtro
            </Button>
          )}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
              <p className="font-medium">Nada para mostrar nesta categoria</p>
              <p className="text-sm mt-1">Tudo sob controle por aqui.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(act => {
              const days = differenceInDays(new Date(act.deadline), new Date());
              const bucket = computeSlaBucket(act);
              return (
                <Card
                  key={act.act_id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/acts/${act.act_id}`)}
                  data-testid={`inbox-act-${act.act_id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-1.5 self-stretch rounded ${
                      bucket === 'vencido' ? 'bg-red-500' :
                      bucket === 'urgente' ? 'bg-orange-500' :
                      bucket === 'proximo' ? 'bg-yellow-500' :
                      bucket === 'concluido' ? 'bg-emerald-500' : 'bg-slate-300'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px]">{act.control_body}</Badge>
                        <span className="text-xs font-mono text-slate-500">{act.act_number || act.act_id.substring(0, 12)}</span>
                        <Badge className={`text-[10px] border ${SEVERITY_STYLES[act.severity] || ''}`}>
                          {act.severity}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-slate-900 truncate">{act.scope || act.description}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(act.deadline), 'dd MMM yyyy', { locale: ptBR })}
                        </span>
                        <span className={
                          bucket === 'vencido' ? 'text-red-600 font-semibold' :
                          bucket === 'urgente' ? 'text-orange-600 font-semibold' : ''
                        }>
                          {bucket === 'concluido' ? STATUS_LABELS[act.status] :
                           days < 0 ? `${Math.abs(days)} dias em atraso` :
                           days === 0 ? 'Vence hoje' : `${days} dias restantes`}
                        </span>
                        <Badge variant="secondary" className="text-[10px]">{STATUS_LABELS[act.status]}</Badge>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default InboxPage;
